<div class="risk-register">

    <!-- The filter and export TRIGGERS live on the parent "Patient Filters" toolbar, following
         `billing-view.component.html:1-40`. Only the MENUS are here, because the state and the
         controls they hold belong to this component — `matMenuTriggerFor` takes a `MatMenu`
         instance, so the parent can open a panel it does not own. See `filterMenu` / `exportMenu`
         exposed as `@ViewChild` below. -->

    <!-- G6 — every control here reads a PLAINTEXT column (D10). The free-text box lives on the
         parent tab and is matched client-side against the decrypted issue/notes. -->
    <mat-menu #filterMenu="matMenu" class="risk-menu-panel">
        <div class="risk-menu" (click)="$event.stopPropagation()">
            <mat-form-field appearance="outline" class="filter-control"
                (click)="$event.stopPropagation()" (keydown)="handleMenuKeydown($event)">
                <mat-label>Facility</mat-label>
                <mat-select [(ngModel)]="hospitalFilter" (selectionChange)="onFilterChange()">
                    <mat-option value="">All facilities</mat-option>
                    <mat-option *ngFor="let option of hospitalOptions" [value]="option.key">{{ option.title }}</mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="filter-control"
                (click)="$event.stopPropagation()" (keydown)="handleMenuKeydown($event)">
                <mat-label>Category</mat-label>
                <mat-select [(ngModel)]="categoryFilter" (selectionChange)="onFilterChange()">
                    <mat-option value="">All categories</mat-option>
                    <mat-option *ngFor="let category of categoryOptions" [value]="category">{{ categoryLabel(category) }}</mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="filter-control"
                (click)="$event.stopPropagation()" (keydown)="handleMenuKeydown($event)">
                <mat-label>Severity</mat-label>
                <mat-select [(ngModel)]="severityFilter" (selectionChange)="onFilterChange()">
                    <mat-option value="">All severities</mat-option>
                    <mat-option *ngFor="let severity of severityOptions" [value]="severity">{{ severityLabel(severity) }}</mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="filter-control"
                (click)="$event.stopPropagation()" (keydown)="handleMenuKeydown($event)">
                <mat-label>Status</mat-label>
                <mat-select [(ngModel)]="statusFilter" (selectionChange)="onFilterChange()">
                    <mat-option value="">All statuses</mat-option>
                    <mat-option *ngFor="let status of statusOptions" [value]="status">{{ status }}</mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="filter-control"
                (click)="$event.stopPropagation()" (keydown)="handleMenuKeydown($event)">
                <mat-label>From</mat-label>
                <input matInput [matDatepicker]="startPicker" [(ngModel)]="startDate" (dateChange)="onFilterChange()">
                <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
                <mat-datepicker #startPicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline" class="filter-control"
                (click)="$event.stopPropagation()" (keydown)="handleMenuKeydown($event)">
                <mat-label>To</mat-label>
                <input matInput [matDatepicker]="endPicker" [(ngModel)]="endDate" (dateChange)="onFilterChange()">
                <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
                <mat-datepicker #endPicker></mat-datepicker>
            </mat-form-field>

            <button mat-stroked-button *ngIf="hasActiveFilters" class="clear-filters"
                (click)="$event.stopPropagation(); clearFilters()">
                <mat-icon>filter_alt_off</mat-icon>
                Clear filters
            </button>
        </div>
    </mat-menu>

    <mat-menu #exportMenu="matMenu" class="risk-menu-panel">
        <div class="risk-menu" (click)="$event.stopPropagation()">
            <!-- The facility the document is FOR. Bound to the same `hospitalFilter` as the
                 filter menu on purpose: the export renders exactly the rows on screen (H3), so a
                 second, independent facility selector here could produce a PDF that disagreed
                 with the register the doctor was looking at when they pressed Export.
                 "All facilities" stays valid - it yields one table per facility (H3). -->
            <mat-form-field appearance="outline" class="filter-control"
                (click)="$event.stopPropagation()" (keydown)="handleMenuKeydown($event)">
                <mat-label>Facility</mat-label>
                <mat-select [(ngModel)]="hospitalFilter" (selectionChange)="onFilterChange()">
                    <mat-option value="">All facilities</mat-option>
                    <mat-option *ngFor="let option of hospitalOptions" [value]="option.key">{{ option.title }}</mat-option>
                </mat-select>
            </mat-form-field>

            <!-- H2/H8. The wording is fixed by §6.H: this is PSEUDONYMISATION, not anonymisation.
                 A row still carries a hospital, a ward, a date and a patient number and is
                 re-identifiable by design. Do not relabel this "Anonymise". -->
            <mat-checkbox [(ngModel)]="redactNames" color="primary"
                (keydown)="handleMenuKeydown($event)">Redact patient names</mat-checkbox>
            <span class="redaction-hint">
                {{ redactNames
                    ? 'Names are masked. Rows are still traceable by the practice from the patient number, facility and date.'
                    : 'Full patient names will be printed on the exported document.' }}
            </span>

            <!-- D16 / §28 P7 — ALL-OR-NOTHING, DEFAULT OFF. Deliberately placed directly beneath
                 "Redact patient names" because it is the same decision about the same document: a
                 photograph can carry a wristband, a chart, a whiteboard or a face, and including
                 one steps straight over the redaction applied to the text beside it.

                 Do not flip this default to on, and do not replace it with a per-image chooser —
                 see `includeImages` in the component for why. -->
            <mat-checkbox [(ngModel)]="includeImages" color="primary"
                (keydown)="handleMenuKeydown($event)">Include attached photographs</mat-checkbox>
            <span class="redaction-hint">
                {{ includeImages
                    ? 'Photographs will be embedded in the document. A photo can show a wristband, a chart or a face, which the name redaction cannot mask.'
                    : 'Photographs are left out. The document states how many were omitted.' }}
            </span>
            <button mat-flat-button color="primary" class="menu-export-button"
                (click)="exportRegister()" [disabled]="exporting || !filteredItems.length">
                <mat-icon>picture_as_pdf</mat-icon>
                {{ exporting ? 'Generating…' : 'Export register (' + filteredItems.length + ')' }}
            </button>
        </div>
    </mat-menu>

    <ng-container *ngIf="sections.length > 0; else noResults">

        <ng-container *ngFor="let section of sections; let i = index">
            <!-- Compressed to the facility name only, centred, matching the inpatients view
                 (`inpatient-list.component.html:33`). The per-category counts are still on the
                 category rows below, and the H3 summary is still on the PDF - this header does
                 not need to repeat either. -->
            <div class="list-header expandable" (click)="toggleHospital(section.key)" [class.m-t-5]="i === 0"
                [matTooltip]="section.nameOnly
                    ? 'Recorded from the admission history. The name did not match a hospital record in this practice, so the issues are grouped by the name as written.'
                    : ''">
                <span class="section-title">{{ section.title }}</span>
                <!-- hospitalId was NULL and the rows are grouped by their recorded name (D12).
                     Legitimate and common, so it is marked rather than hidden - without it the
                     same facility appearing twice reads as a bug. -->
                <mat-icon class="section-note-icon" *ngIf="section.nameOnly">help_outline</mat-icon>
                <mat-icon aria-hidden="false">
                    {{ expandedHospital === section.key ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </div>

            <div @expandCollapse *ngIf="expandedHospital === section.key" class="section-body">

                <ng-container *ngFor="let categorySection of section.categories">
                    <div class="category-header">
                        {{ categorySection.label }} ({{ categorySection.items.length }})
                    </div>

                    <div class="risk-row" *ngFor="let item of categorySection.items" (click)="openRiskItem(item)">
                        <div class="risk-row-details">
                            <div class="risk-row-top">
                                <span class="severity-indicator" [ngClass]="item.severity"
                                    [matTooltip]="'Severity: ' + severityLabel(item.severity)"></span>
                                <span class="risk-issue">{{ item.issue }}</span>
                            </div>
                            <div class="risk-row-meta">
                                <span>{{ item.dateOfIssue | date : 'dd/MM/yyyy' }}</span>
                                <span class="dot">·</span>
                                <span>{{ item.wardName || locationLabel(item.location) }}</span>
                                <span class="dot">·</span>
                                <!-- patientId is NULLABLE (§5c). "No patient" is a normal row. -->
                                <span>{{ patientLabel(item) }}</span>
                                <span class="patient-number" *ngIf="item.Patient?.patientNumber">({{ item.Patient.patientNumber }})</span>
                                <span class="dot">·</span>
                                <span class="status-badge" [ngClass]="item.status?.toLowerCase()">{{ item.status }}</span>
                                <span class="reported-at" *ngIf="item.reportedAt">reported {{ item.reportedAt | date : 'dd/MM/yyyy' }}</span>
                            </div>

                            <!-- §28 / P6 — the attached photographs, as a thumbnail strip.

                                 `app-file-gallery` accepts RAW SERVER MEDIA: its `ngOnChanges`
                                 detects a `mediaUrl` on the first element and runs
                                 `mapMediaToFileObject` itself, so `item.media` goes straight in
                                 with no adapter here. `deleteEnabled` is false — the register is a
                                 list view and evidence must not be removable by a stray tap on a
                                 row whose whole surface is already a click target.

                                 That click target is also why this sits inside `.risk-row-details`
                                 and stays non-interactive: the row's `(click)` opens the item, and
                                 the gallery's own maximise handler already calls
                                 `stopPropagation()`, so tapping a thumbnail opens the image rather
                                 than the item — which is the behaviour a doctor expects from a
                                 photograph. -->
                            <div class="risk-row-media" *ngIf="item.media?.length">
                                <app-file-gallery [fileObjects]="item.media" [deleteEnabled]="false">
                                </app-file-gallery>
                            </div>
                        </div>

                        <!-- G5 — status transitions inline, per D9. -->
                        <div class="action-icons keep-colors">
                            <button mat-icon-button color="primary" *ngIf="item.status !== 'Reported'"
                                (click)="updateStatus($event, item, 'Reported')"
                                matTooltip="Mark as reported to the facility">
                                <mat-icon>outgoing_mail</mat-icon>
                            </button>
                            <button mat-icon-button color="primary" *ngIf="item.status !== 'Resolved'"
                                (click)="updateStatus($event, item, 'Resolved')" matTooltip="Mark as resolved">
                                <mat-icon>check_circle</mat-icon>
                            </button>
                            <button mat-icon-button color="warn" *ngIf="item.status !== 'Dismissed'"
                                (click)="updateStatus($event, item, 'Dismissed')" matTooltip="Dismiss this issue">
                                <mat-icon>cancel</mat-icon>
                            </button>
                            <button mat-icon-button *ngIf="item.status !== 'Open'"
                                (click)="updateStatus($event, item, 'Open')" matTooltip="Reopen this issue">
                                <mat-icon>replay</mat-icon>
                            </button>
                            <button mat-icon-button *ngIf="item.patientId"
                                (click)="navigateToPatientFile($event, item.patientId)"
                                matTooltip="Load the patient file">
                                <mat-icon>visibility</mat-icon>
                            </button>
                        </div>
                    </div>
                </ng-container>

            </div>
        </ng-container>

        <div class="mobile-spacer"></div>
    </ng-container>

</div>

<ng-template #noResults>
    <p class="no-records-message">No risk items found.</p>
</ng-template>
